import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';
import { downloadCV } from '../../utils/terminalUtils';
import { useScrollSpy } from './hooks/useScrollSpy';

// Sections
import Hero from './sections/Hero';
import About from './sections/About';
import Skills from './sections/Skills';
import Experience from './sections/Experience';
import Projects from './sections/Projects';
import Education from './sections/Education';
import Footer from './sections/Footer';

// Styles
import {
  ClassicPageContainer,
  Header,
  HeaderContainer,
  HeaderLogo,
  HeaderNav,
  NavLink,
  HeaderRight,
  HeaderButton,
  MenuToggle,
  MobileMenuOverlay,
  MobileNavLink,
  ContentContainer,
  TerminalFAB,
} from './styles';

interface ClassicPageProps {
  onSwitchToTerminal: () => void;
  onSwitchToServices: () => void;
}

const ClassicPage: React.FC<ClassicPageProps> = ({ onSwitchToTerminal, onSwitchToServices }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useThemeStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { activeSection, scrollToSection } = useScrollSpy();

  // Track scroll for header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen]);

  // Hash-based initial scroll
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['about', 'skills', 'experience', 'projects', 'education'].includes(hash)) {
      setTimeout(() => scrollToSection(hash), 300);
    }
  }, [scrollToSection]);

  const handleDownloadCV = useCallback(() => {
    downloadCV(i18n.language === 'tr' ? 'tr' : 'en');
  }, [i18n.language]);

  const handleNavClick = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    scrollToSection(id);
    if (menuOpen) setMenuOpen(false);
  }, [scrollToSection, menuOpen]);

  const navItems = [
    { id: 'about', label: t('ClassicPage.nav.about') },
    { id: 'skills', label: t('ClassicPage.nav.skills') },
    { id: 'experience', label: t('ClassicPage.nav.experience') },
    { id: 'projects', label: t('ClassicPage.nav.projects') },
    { id: 'education', label: t('ClassicPage.nav.education') },
  ];

  return (
    <ClassicPageContainer $themeName={theme}>
      {/* Slim Sticky Header */}
      <Header $themeName={theme} $scrolled={scrolled}>
        <HeaderContainer>
          <HeaderLogo $themeName={theme}>Anıl ORHAN</HeaderLogo>

          <HeaderNav>
            {navItems.map(item => (
              <NavLink
                key={item.id}
                $themeName={theme}
                $active={activeSection === item.id}
                href={`#${item.id}`}
                onClick={(e) => handleNavClick(e, item.id)}
              >
                {item.label}
              </NavLink>
            ))}
          </HeaderNav>

          <HeaderRight>
            <HeaderButton $themeName={theme} $variant="primary" onClick={handleDownloadCV}>
              {t('ClassicPage.nav.downloadCV')}
            </HeaderButton>
            <MenuToggle $themeName={theme} $isOpen={menuOpen} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              <span /><span /><span />
            </MenuToggle>
          </HeaderRight>
        </HeaderContainer>
      </Header>

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $themeName={theme} $isOpen={menuOpen}>
        {navItems.map(item => (
          <MobileNavLink
            key={item.id}
            $themeName={theme}
            href={`#${item.id}`}
            onClick={(e) => handleNavClick(e, item.id)}
          >
            {item.label}
          </MobileNavLink>
        ))}
        <MobileNavLink $themeName={theme} href="#" onClick={(e) => { e.preventDefault(); onSwitchToServices(); }}>
          {t('ClassicPage.switchToServices')}
        </MobileNavLink>
      </MobileMenuOverlay>

      {/* Hero Section */}
      <Hero
        themeName={theme}
        onSwitchToTerminal={onSwitchToTerminal}
        onSwitchToServices={onSwitchToServices}
        onDownloadCV={handleDownloadCV}
      />

      {/* Content Sections */}
      <ContentContainer>
        <About themeName={theme} />
        <Skills themeName={theme} />
        <Experience themeName={theme} />
        <Projects themeName={theme} />
        <Education themeName={theme} />
      </ContentContainer>

      {/* Footer */}
      <Footer themeName={theme} onScrollToSection={scrollToSection} />

      {/* Terminal FAB */}
      <TerminalFAB
        $themeName={theme}
        onClick={onSwitchToTerminal}
        title={t('ClassicPage.switchToTerminal')}
        aria-label={t('ClassicPage.switchToTerminal')}
      >
        &#x1F5B3;
      </TerminalFAB>
    </ClassicPageContainer>
  );
};

export default ClassicPage;
